import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://iuekmsrjocpvranvngko.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml1ZWttc3Jqb2NwdnJhbnZuZ2tvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM2MDQ1MzgsImV4cCI6MjA4OTE4MDUzOH0.cDL0v7XjjErR-gXVAAMSPNstB4EvypXHDHJsGAmo2YU';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─── Admin Auth ───────────────────────────────────────────────────────────────

export async function fetchAdminRole(userId) {
  const { data, error } = await supabase.from('admins').select('role').eq('user_id', userId).single();
  if (error) return null; // not logged in as an admin, or no matching row — treated the same
  return data?.role || null;
}

// ─── Shared Articles ──────────────────────────────────────────────────────────

export async function fetchSharedArticle(code) {
  const { data, error } = await supabase.from('shared_articles').select('data').eq('code', code).single();
  if (error) { console.error('fetchSharedArticle:', error); return null; }
  return data?.data || null;
}

// ─── Hidden Articles ──────────────────────────────────────────────────────────

export async function fetchHiddenIds() {
  const { data, error } = await supabase.from('hidden_articles').select('id');
  if (error) { console.error('fetchHiddenIds:', error); return new Set(); }
  return new Set(data.map(r => r.id));
}

export async function hideArticle(id) {
  const { error } = await supabase.from('hidden_articles').upsert({ id });
  if (error) console.error('hideArticle:', error);
}

export async function unhideArticle(id) {
  const { error } = await supabase.from('hidden_articles').delete().eq('id', id);
  if (error) console.error('unhideArticle:', error);
}

// ─── Edited Articles ──────────────────────────────────────────────────────────

export async function fetchEditedArticles() {
  const { data, error } = await supabase.from('edited_articles').select('id, data');
  if (error) { console.error('fetchEditedArticles:', error); return []; }
  return data.map(r => r.data);
}

export async function saveEditedArticle(article) {
  const { error } = await supabase.from('edited_articles').upsert({ id: article.id, data: article });
  if (error) console.error('saveEditedArticle:', error);
}

export async function deleteEditedArticle(id) {
  const { error } = await supabase.from('edited_articles').delete().eq('id', id);
  if (error) console.error('deleteEditedArticle:', error);
}

// ─── Custom Articles ──────────────────────────────────────────────────────────

export async function fetchCustomArticles() {
  const { data, error } = await supabase
    .from('custom_articles')
    .select('id, data, is_sponsored, is_draft')
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchCustomArticles:', error); return []; }
  return data.map(r => ({ ...r.data, id: r.id, isSponsored: r.is_sponsored, isDraft: r.is_draft }));
}

export async function saveCustomArticle(article) {
  const { error } = await supabase.from('custom_articles').upsert({
    id: article.id,
    data: article,
    is_sponsored: article.isSponsored || false,
    is_draft: article.isDraft || false,
  });
  if (error) console.error('saveCustomArticle:', error);
}

export async function deleteCustomArticle(id) {
  const { error } = await supabase.from('custom_articles').delete().eq('id', id);
  if (error) console.error('deleteCustomArticle:', error);
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

export async function logAction(userName, action) {
  const { error } = await supabase.from('audit_log').insert({ user_name: userName, action });
  if (error) console.error('logAction:', error);
}

export async function fetchAuditLog() {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) { console.error('fetchAuditLog:', error); return []; }
  return data;
}

export async function clearAuditLog() {
  const { error } = await supabase.from('audit_log').delete().neq('id', 0);
  if (error) console.error('clearAuditLog:', error);
}

// ─── Config (admin-only key/value settings) ────────────────────────────────────

export async function fetchAdsEnabledConfig() {
  const { data, error } = await supabase.from('config').select('value').eq('key', 'ads_enabled').maybeSingle();
  if (error) { console.error('fetchAdsEnabledConfig:', error); return true; }
  return data ? data.value === 'true' : true; // no row yet — default to ads on
}

export async function saveAdsEnabledConfig(enabled) {
  const { error } = await supabase.from('config')
    .upsert({ key: 'ads_enabled', value: String(enabled), updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) console.error('saveAdsEnabledConfig:', error);
}
